import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import {
  type ParsedPolicyIndication,
  parsePolicyToIndication,
} from '../../../../../models/insuranceIndication'
import { InsuranceOfferKind } from '../../../../../models/insuranceTypes'

interface ParsePolicyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
  /** Which record the parsed fields will go on to create. */
  kind: InsuranceOfferKind
  /** `kind` is echoed back so it always describes the document that was parsed. */
  onParsed: (parsed: ParsedPolicyIndication, kind: InsuranceOfferKind) => void
}

export function ParsePolicyDialog({ open, onOpenChange, orgId, kind, onParsed }: ParsePolicyDialogProps) {
  const [file, setFile] = useState<File | null>(null)
  const [isParsing, setIsParsing] = useState(false)
  const runId = useRef(0)

  // The dialog stays mounted across opens, so closing it has to reset the state
  // itself and abandon any parse still in flight.
  useEffect(() => {
    if (!open) {
      setFile(null)
      setIsParsing(false)
      runId.current += 1
    }
  }, [open])

  const handleParse = async () => {
    if (!file) return
    const run = ++runId.current
    setIsParsing(true)
    try {
      const parsed = await parsePolicyToIndication(orgId, file)
      if (run !== runId.current) return
      onParsed(parsed, kind)
      onOpenChange(false)
      setFile(null)
    } catch {
      if (run === runId.current) toast.error('Failed to parse the document. Please try again.')
    } finally {
      if (run === runId.current) setIsParsing(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kind === InsuranceOfferKind.QUOTE ? 'Upload quote' : 'Upload indication'}
          </DialogTitle>
          <DialogDescription>
            Upload a PDF and we&apos;ll parse its coverages to prefill a new{' '}
            {kind === InsuranceOfferKind.QUOTE ? 'quote' : 'indication'}. Parsing can take up to a minute.
            Accepted formats: PDF.
          </DialogDescription>
        </DialogHeader>
        <Dropzone onChange={setFile} accept=".pdf" isLoading={isParsing} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isParsing}>
            Cancel
          </Button>
          <Button onClick={handleParse} loading={isParsing ? 'Parsing…' : undefined} disabled={!file}>
            Parse
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
